import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { IonSearchbar, ModalController } from '@ionic/angular';
import {
  finalize,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
} from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { HotelBookingService } from 'src/app/services/hotel-booking/hotel-booking.service';
import { UiService } from 'src/app/services/ui.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';

export interface SuggestionItem {
  type: string;
  title: string;
  city?: string;
  country?: string;
  entityId?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-auto-suggestion-modal',
  templateUrl: './auto-suggestion-modal.component.html',
  styleUrls: ['./auto-suggestion-modal.component.scss'],
})
export class AutoSuggestionModalComponent implements OnInit, OnDestroy {
  @ViewChild('hotelSearch', { static: false }) searchbar: IonSearchbar;
  public suggestionList: SuggestionItem[];
  public currentHeaderData: any = {
    modalSlotEnd: true,
    marl20: true,
    title: 'hotel.auto_suggestion_modal.header',
    fs4: true,
  };

  page: number;
  noData: boolean;
  loading: boolean;
  searchTerm: string;
  lastPage: boolean;

  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;

  constructor(
    private modalController: ModalController,
    private hotelBookingService: HotelBookingService,
    private uiService: UiService,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.initPageState();
    this.setupSearchDebounce();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
  }

  setupSearchDebounce() {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((term) => term.trim()),
        switchMap((searchTerm) => {
          this.searchTerm = searchTerm;
          this.initPageState();
          this.loading = true;

          return this.hotelBookingService
            .getCityHotelSuggestions(this.searchTerm, this.page)
            .pipe(finalize(() => (this.loading = false)));
        })
      )
      .subscribe((response) => {
        if (response?.last) {
          this.lastPage = true;
        }
        if (response?.content?.length > 0) {
          this.suggestionList = response.content;
        } else {
          this.noData = true;
        }
      },
      (error) => {
        this.analyticsService.trackResponseError(error, 'Hotel Search Error');
      }
    );
  }

  initPageState() {
    this.suggestionList = [];
    this.page = 0;
    this.noData = false;
    this.loading = false;
    this.lastPage = false;
  }
  selectSuggestion(item: SuggestionItem) {
    this.modalController.dismiss({
      ...item,
    });
  }
  closeModal() {
    this.analyticsService.trackUnified('search_input_closed', {
      flow: 'hotel',
      outcome: 'abandon',
      legacy_event_name: 'Hotel Search Close',
      search: this.searchTerm,
      button: 'search-close',
    });
    this.modalController.dismiss();
  }
  async getAutoSuggestions() {
    this.initPageState();
    this.loading = true;
    this.hotelBookingService
      .getCityHotelSuggestions(this.searchTerm, this.page)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((response) => {
        if (response?.last) {
          this.lastPage = true;
        }
        if (response?.content?.length > 0) {
          this.suggestionList = response.content;
        } else {
          this.noData = true;
        }
      },
      (error) => {
        this.analyticsService.trackResponseError(error, 'Hotel Search Error');
      }
    );
  }

  getNextPage(event) {
    this.page++;
    this.hotelBookingService
      .getCityHotelSuggestions(this.searchTerm, this.page)
      .pipe(
        finalize(() => {
          event.target.complete();
        })
      )
      .subscribe((response) => {
        if (response?.last) {
          event.target.disabled = true;
        }
        if (response?.content?.length > 0) {
          this.suggestionList = this.suggestionList.concat(response.content);
        }
      },
      (error) => {
        this.analyticsService.trackResponseError(error, 'Hotel Search Error');
      }
    );
  }

  filterLIst(event) {
    const searchTerm = event.srcElement.value.trim();
    this.searchSubject.next(searchTerm);
  }

  filterListOnEnter(event) {
    const searchTerm = event.srcElement.value.trim();
    this.searchTerm = searchTerm;
    this.getAutoSuggestions();
  }

  ionViewDidEnter(): void {
    try {
      this.searchbar?.setFocus();
    } catch (e) {}
  }
}
